import { useEffect, useRef, useState } from "react";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import Modal from "../common/Modal";
import InitialsAvatar from "../common/InitialsAvatar";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../hooks/useToast";
import api from "../../services/api";
import styles from "./TicketDetailModal.module.css";

const HISTORY_LIMIT = 10;

const ACTOR_TYPE_BADGES = {
  system: "System",
  automation: "Automation",
  workflow: "Workflow",
  integration: "Integration",
  email_api: "Email",
};

const formatValue = (value) =>
  value ? value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "";

const describeEvent = (event) => {
  const { eventType, previousValue, newValue, metadata } = event;
  switch (eventType) {
    case "ticket_created":
      return "Ticket created";
    case "status_changed":
      return `Status changed from ${formatValue(previousValue)} to ${formatValue(newValue)}`;
    case "priority_changed":
      return `Priority changed from ${formatValue(previousValue)} to ${formatValue(newValue)}`;
    case "category_changed":
      return `Category changed from ${formatValue(previousValue)} to ${formatValue(newValue)}`;
    case "reply_sent":
      return "Reply sent";
    case "email_sent":
      return `Email sent to ${metadata?.recipient || "member"}`;
    case "email_failed":
      return `Email failed to ${metadata?.recipient || "member"}`;
    case "customer_replied":
      return "Customer replied";
    case "internal_note_added":
      return "Internal note added";
    case "assigned":
      return newValue ? `Assigned to ${newValue}` : "Unassigned";
    case "reassigned":
      return `Reassigned from ${previousValue || "unassigned"} to ${newValue || "unassigned"}`;
    case "ownership_claimed":
      return "Ownership claimed";
    case "ticket_resolved":
      return "Ticket resolved";
    case "ticket_closed":
      return "Ticket closed";
    case "ticket_reopened":
      return "Ticket reopened";
    case "escalated":
      return "Escalated to urgent";
    case "linked_to_order":
      return "Linked to order";
    case "linked_to_refill":
      return "Linked to refill request";
    default:
      return formatValue(eventType);
  }
};

const isInternalEvent = (event) =>
  event.eventType === "internal_note_added" || event.metadata?.isInternal === true;

const deriveClaimedAt = (events) => {
  const claimEvent = events.find(
    (event) =>
      event.eventType === "ownership_claimed" ||
      (event.eventType === "assigned" && event.newValue)
  );
  return claimEvent ? claimEvent.createdAt : null;
};

const getPriorityClass = (priority) => {
  const priorityMap = {
    low: styles.priorityLow,
    normal: styles.priorityNormal,
    medium: styles.priorityNormal,
    high: styles.priorityHigh,
    urgent: styles.priorityUrgent,
  };
  return priorityMap[priority] || styles.priorityNormal;
};

/**
 * Shared ticket detail modal — the single source of truth for the full
 * interactive ticket view (status control, meta, conversation thread, reply
 * box, and history). Used by BOTH the Support Tickets page and the client
 * profile so the two entry points can never drift.
 *
 * Fetches its own detail + history from `ticketId`; calls `onUpdated` after a
 * status change or reply so the parent list can refresh.
 */
const TicketDetailModal = ({ ticketId, isOpen, onClose, onUpdated }) => {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [claiming, setClaiming] = useState(false);
  const claimInFlight = useRef(false);
  const [sendingReply, setSendingReply] = useState(false);
  const replyInFlight = useRef(false);

  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const loadTicket = async (id) => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/support-tickets/${id}`);
      setTicket(response.ticket);
      return response.ticket;
    } catch (error) {
      console.error("Failed to load ticket:", error);
      toast.error("Failed to load ticket");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (id, page = 1) => {
    setHistoryLoading(true);
    try {
      const response = await api.get(
        `/admin/support-tickets/${id}/history?page=${page}&limit=${HISTORY_LIMIT}`
      );
      const events = response.events || [];
      setHistoryEvents((prev) => (page === 1 ? events : [...prev, ...events]));
      setHistoryTotal(response.total || 0);
      setHistoryPage(page);
      setHistoryError(null);
    } catch (error) {
      console.error("Failed to load ticket history:", error);
      setHistoryError("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && ticketId) {
      setTicket(null);
      setReplyText("");
      setHistoryEvents([]);
      setHistoryTotal(0);
      setHistoryPage(1);
      setHistoryError(null);
      loadTicket(ticketId);
      loadHistory(ticketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticketId]);

  const handleUpdateStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/support-tickets/${ticketId}/status`, {
        status: newStatus,
      });
      toast.success("Ticket status updated");
      setTicket((prev) => (prev ? { ...prev, status: newStatus } : prev));
      loadHistory(ticketId);
      onUpdated?.();
    } catch (error) {
      toast.error("Failed to update ticket status");
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    // The ref latches synchronously, so a second click in the SAME tick is
    // rejected too -- `sendingReply` alone cannot do that, because React has
    // not re-rendered the disabled button yet.
    if (replyInFlight.current) return;
    replyInFlight.current = true;
    setSendingReply(true);
    try {
      await api.post(`/admin/support-tickets/${ticketId}/messages`, {
        content: replyText,
      });
      toast.success("Reply sent successfully");
      setReplyText("");
      await loadTicket(ticketId);
      loadHistory(ticketId);
      onUpdated?.();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      replyInFlight.current = false;
      setSendingReply(false);
    }
  };

  const handleClaimTicket = async () => {
    if (claimInFlight.current) return;
    claimInFlight.current = true;
    setClaiming(true);
    try {
      await api.post(`/admin/support-tickets/${ticketId}/claim`);
      toast.success("Ticket claimed");
      await loadTicket(ticketId);
      loadHistory(ticketId);
      onUpdated?.();
    } catch (error) {
      if (error.status === 403) {
        // First-claim-wins: another agent already owns it. Re-fetch so the
        // detail shows the actual owner (the 403 body drops it) instead of
        // the stale Unclaimed state, and never overwrite the first claim.
        const current = await loadTicket(ticketId);
        loadHistory(ticketId);
        const owner = current?.assignedTo?.fullName;
        toast.error(
          owner
            ? `Already claimed by ${owner}`
            : "This ticket is already claimed by another agent"
        );
        onUpdated?.();
      } else {
        toast.error("Failed to claim ticket");
      }
    } finally {
      claimInFlight.current = false;
      setClaiming(false);
    }
  };

  const claimedAt = deriveClaimedAt(historyEvents);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        ticket
          ? `Ticket #${ticket.ticketNumber}: ${ticket.subject}`
          : "Ticket Details"
      }
      size="large"
    >
      {loading && !ticket ? (
        <div className={styles.modalLoading}>
          <LoadingSpinner />
        </div>
      ) : ticket ? (
        <div className={styles.ticketDetail}>
          <div className={styles.ticketMeta}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Status</span>
              <select
                className={styles.statusSelect}
                value={ticket.status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Priority</span>
              <span
                className={`${styles.priorityBadge} ${getPriorityClass(ticket.priority)}`}
              >
                {ticket.priority}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Category</span>
              <span>{ticket.category}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Created</span>
              <span>{new Date(ticket.createdAt).toLocaleString()}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Claimed by</span>
              {ticket.assignedTo ? (
                <div className={styles.claimedBy}>
                  <InitialsAvatar
                    firstName={ticket.assignedTo.firstName}
                    lastName={ticket.assignedTo.lastName}
                    size="sm"
                  />
                  <span>
                    {ticket.assignedTo.fullName}
                    {claimedAt
                      ? ` · ${new Date(claimedAt).toLocaleString()}`
                      : ""}
                  </span>
                </div>
              ) : (
                <div className={styles.unclaimedRow}>
                  <span className={styles.unclaimedText}>Unclaimed</span>
                  {hasPermission("support_tickets.edit") && (
                    <Button
                      size="small"
                      loading={claiming}
                      onClick={handleClaimTicket}
                    >
                      Claim Ticket
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.ticketUser}>
            <span className={styles.metaLabel}>From</span>
            <div className={styles.userInfo}>
              <span className={styles.userName}>
                {ticket.user?.firstName} {ticket.user?.lastName}
              </span>
              <span className={styles.userEmail}>{ticket.user?.email}</span>
            </div>
          </div>

          {/* Messages Thread */}
          {ticket.messages && ticket.messages.length > 0 && (
            <div className={styles.messagesSection}>
              <span className={styles.metaLabel}>Conversation</span>
              <div className={styles.messagesList}>
                {ticket.messages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`${styles.message} ${msg.senderType === "admin" ? styles.adminMessage : styles.userMessage}`}
                  >
                    <div className={styles.messageHeader}>
                      <span className={styles.messageSender}>
                        {msg.sender?.firstName || "User"}{" "}
                        {msg.sender?.lastName || ""}
                        {msg.senderType === "admin" && " (Admin)"}
                      </span>
                      <span className={styles.messageTime}>
                        {new Date(msg.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className={styles.messageContent}>{msg.content}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.replySection}>
            <span className={styles.metaLabel}>Reply</span>
            <textarea
              className={styles.replyTextarea}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              rows={4}
            />
            <div className={styles.replyActions}>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button
                onClick={handleSendReply}
                loading={sendingReply}
                disabled={!replyText.trim()}
              >
                Send Reply
              </Button>
            </div>
          </div>

          {/* History */}
          <div className={styles.historySection}>
            <span className={styles.metaLabel}>History</span>
            {historyLoading && historyEvents.length === 0 ? (
              <div className={styles.historyLoading}>
                <LoadingSpinner size="small" />
              </div>
            ) : historyError && historyEvents.length === 0 ? (
              <div className={styles.historyError}>
                {historyError}{" "}
                <button
                  className={styles.historyRetry}
                  onClick={() => loadHistory(ticketId)}
                >
                  Retry
                </button>
              </div>
            ) : historyEvents.length === 0 ? (
              <div className={styles.historyEmpty}>No history yet</div>
            ) : (
              <>
                <div className={styles.historyList}>
                  {historyEvents.map((event) => (
                    <div key={event.id} className={styles.historyItem}>
                      <div className={styles.historyTop}>
                        <span className={styles.historyText}>
                          {describeEvent(event)}
                        </span>
                        {isInternalEvent(event) && (
                          <span
                            className={`${styles.historyBadge} ${styles.badgeInternal}`}
                          >
                            Internal
                          </span>
                        )}
                        {event.eventType === "email_sent" && (
                          <span
                            className={`${styles.historyBadge} ${styles.badgeDelivered}`}
                          >
                            Delivered
                          </span>
                        )}
                        {event.eventType === "email_failed" && (
                          <span
                            className={`${styles.historyBadge} ${styles.badgeFailed}`}
                          >
                            Failed
                          </span>
                        )}
                      </div>
                      {event.metadata?.preview && (
                        <div className={styles.historyPreview}>
                          {event.metadata.preview}
                        </div>
                      )}
                      <div className={styles.historyMetaLine}>
                        {event.actorDisplayName ? (
                          <span className={styles.historyActor}>
                            {event.actorDisplayName}
                            {event.actorRole ? ` · ${event.actorRole}` : ""}
                          </span>
                        ) : (
                          <span
                            className={`${styles.historyBadge} ${styles.badgeActor}`}
                          >
                            {ACTOR_TYPE_BADGES[event.actorType] || "System"}
                          </span>
                        )}
                        <span className={styles.historyTime}>
                          {new Date(event.createdAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {historyError && (
                  <div className={styles.historyError}>{historyError}</div>
                )}
                {historyEvents.length < historyTotal && (
                  <div className={styles.historyLoadMore}>
                    <Button
                      variant="outline"
                      size="small"
                      loading={historyLoading}
                      onClick={() => loadHistory(ticketId, historyPage + 1)}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default TicketDetailModal;
